import { FormEvent, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { servicesForRegion } from "@shared/types";
import {
  STANDARD_EA_MINING,
  isStandardEaMiningServiceParam,
  standardEaMiningMoney,
} from "@shared/standardEaMining";
import { AUDIENCES } from "../data/audiences";
import { api } from "../lib/api";
import { TurnstileWidget } from "../components/Turnstile";
import { AudienceIcon } from "../components/Visuals";

export function ContactPage() {
  const [params] = useSearchParams();
  const audienceParam = params.get("audience") || "";
  const serviceParam = params.get("service") || "";
  const matched = AUDIENCES.find((a) => a.id === audienceParam);
  const standardEaFromQuery = isStandardEaMiningServiceParam(serviceParam);
  const standardEaMoney = standardEaMiningMoney();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    companyName: "",
    phone: "",
    serviceInterest: serviceParam || matched?.name || "",
    projectLocation: "",
    message: serviceParam
      ? standardEaFromQuery
        ? `${STANDARD_EA_MINING.name}. ${STANDARD_EA_MINING.priceLine}.\n\nProject context:\n`
        : `DIFY consultancy enquiry — ${serviceParam}\n\nProject context:\n`
      : matched
        ? `Enquiry as ${matched.name}: ${matched.headline}`
        : "",
  });
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const onTurnstileToken = useCallback((t: string | null) => setTurnstileToken(t), []);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (serviceParam) {
      setForm((f) => ({
        ...f,
        serviceInterest: serviceParam,
        message:
          f.message && f.message.includes(serviceParam)
            ? f.message
            : standardEaFromQuery
              ? `${STANDARD_EA_MINING.name}. ${STANDARD_EA_MINING.priceLine}.\n\nProject context:\n`
              : `DIFY consultancy enquiry — ${serviceParam}\n\nProject context:\n`,
      }));
      return;
    }
    if (!matched) return;
    setForm((f) => ({
      ...f,
      serviceInterest: f.serviceInterest || matched.name,
      message:
        f.message ||
        `Enquiry as ${matched.name}: ${matched.headline}\n\nProject context:\n`,
    }));
  }, [matched, serviceParam, standardEaFromQuery]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.enquiry({ ...form, "cf-turnstile-response": turnstileToken ?? "" });
      setOk(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send");
      setTurnstileToken(null);
      setTurnstileAttempt((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section">
      <div className="container grid-2">
        <div>
          <h2>Tell us what’s blocking the project</h2>
          <p className="muted">
            Use this form for complex or multi-agency work (consultancy from A$7,500 ex GST). If a
            free tool or ~48-hour guided report will fix it, we’ll say so — and save you the
            proposal cycle. Ecological Engineering · EcoEng.net.
          </p>
          <p className="muted" style={{ marginTop: "0.75rem" }}>
            Need something faster?{" "}
            <a href="/resources">Download tools</a> · <a href="/reports">Order a guided report</a>
          </p>
          {standardEaFromQuery || isStandardEaMiningServiceParam(form.serviceInterest) ? (
            <div className="alert alert-info" style={{ marginTop: "1rem" }}>
              <strong>{STANDARD_EA_MINING.name}</strong>
              <p style={{ margin: "0.4rem 0 0" }}>
                {standardEaMoney.plusGstLine}. GST {standardEaMoney.gst}. Total{" "}
                {standardEaMoney.incl} incl. GST. Enquiry first. We confirm scope before work
                starts.
              </p>
            </div>
          ) : null}

          {matched ? (
            <div
              className="card"
              style={{
                marginTop: "1rem",
                display: "grid",
                gridTemplateColumns: "56px 1fr",
                gap: "0.85rem",
                alignItems: "start",
                color: matched.accent,
              }}
            >
              <AudienceIcon id={matched.id} className="audience-card-icon" />
              <div style={{ color: "var(--ink)" }}>
                <div className="eyebrow" style={{ marginBottom: "0.4rem" }}>
                  {matched.name}
                </div>
                <p style={{ margin: 0 }}>{matched.blurb}</p>
              </div>
            </div>
          ) : null}

          <div className="card" style={{ marginTop: "1rem" }}>
            <h3>Sectors we support</h3>
            <div className="tag-row" style={{ marginTop: "0.5rem" }}>
              {AUDIENCES.map((a) => (
                <span key={a.id} className="tag">
                  {a.name}
                </span>
              ))}
            </div>
          </div>
        </div>
        <form className="card" onSubmit={(e) => void onSubmit(e)}>
          {ok ? (
            <div className="alert alert-success">
              Enquiry received. We will follow up by email shortly.
            </div>
          ) : null}
          {error ? <div className="alert alert-error">{error}</div> : null}
          <label>
            Full name
            <input
              required
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </label>
          <label>
            Email
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <div className="form-row">
            <label>
              Company
              <input
                value={form.companyName}
                onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              />
            </label>
            <label>
              Phone
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>
          </div>
          <label>
            Sector / service interest
            <select
              value={form.serviceInterest}
              onChange={(e) => setForm({ ...form, serviceInterest: e.target.value })}
            >
              <option value="">Select…</option>
              <optgroup label="Sectors">
                {AUDIENCES.map((a) => (
                  <option key={a.id} value={a.name}>
                    {a.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Australia services">
                {servicesForRegion("au").map((s) => (
                  <option key={s.id} value={`AU · ${s.name}`}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Singapore services">
                {servicesForRegion("sg").map((s) => (
                  <option key={s.id} value={`SG · ${s.name}`}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
              <option value="Other">Other</option>
            </select>
          </label>
          <label>
            Project location
            <input
              value={form.projectLocation}
              onChange={(e) => setForm({ ...form, projectLocation: e.target.value })}
            />
          </label>
          <label>
            Message
            <textarea
              required
              minLength={10}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </label>
          <TurnstileWidget key={turnstileAttempt} onToken={onTurnstileToken} />
          <button className="btn btn-primary" type="submit" disabled={busy || ok}>
            {busy ? "Sending…" : "Send enquiry"}
          </button>
        </form>
      </div>
    </section>
  );
}
